// Caption presets: one click in the export window sets the caption knobs (font, colours, size,
// caps, look, words, highlight, appear, bounce) to a ready-made look. A preset with an `engine`
// is a layout of its own that the knobs can't make (Story Stack), drawn by the worker's
// caption_fx and, here, by ExportPreview's StackCaption.
//
// The worker's copy is emo/transcript/caption_presets.json (+ the Story Stack rules in
// caption_fx.py). captionPresets.test.js checks this file against both.

/** `fields` use make-video's names (what My Style keeps) — myStyle.styleToWindow turns them into
 *  the window's choices. */
export const CAPTION_PRESETS = [
  {
    key: 'stack',
    label: 'Story Stack',
    desc: 'Words stack down the left as they are said. Key words come in bigger.',
    engine: 'stack',
    fields: { caption_font_key: 'montblack', caption_style: 'white', caption_size: 64, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 3, caption_highlight: 'color', caption_reveal: 'all', caption_bounce: 'none' },
  },
  {
    key: 'podbox',
    label: 'Podcast Box',
    desc: 'Two lines of heavy capitals. A purple box glides to each word as it is said.',
    engine: 'box',
    fields: { caption_font_key: 'montblack', caption_style: 'violet', caption_size: 84, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'box', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'box', motion: 'glide', ms: 150, group: 6, line: 1.18, radius: 0.2, pad: [0.14, 0.04], spot: [0.74, 0.8] },
  },
  {
    key: 'podrise',
    label: 'Soft Rise',
    desc: 'Heavy capitals. Each word\'s own purple box rises in, quick and smooth.',
    engine: 'box',
    fields: { caption_font_key: 'montblack', caption_style: 'violet', caption_size: 84, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'box', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'box', motion: 'rise', ms: 120, drop: 0.22, group: 6, line: 1.18, radius: 0.24, pad: [0.16, 0.04], spot: [0.74, 0.8] },
  },
  {
    key: 'podwhisper',
    label: 'Whisper',
    desc: 'Calm sentence case. A soft see-through box fades onto each word.',
    engine: 'box',
    fields: { caption_font_key: 'plain', caption_style: 'violet', caption_size: 64, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'box', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'box', motion: 'fade', ms: 200, group: 6, line: 1.3, radius: 0.4, pad: [0.2, 0.02], alpha: 0.55, spot: [0.74, 0.8] },
  },
  {
    key: 'podcherry',
    label: 'Cherry Box',
    desc: 'Heavy capitals. A cherry box with a faint red glow rises onto each word.',
    engine: 'box',
    fields: { caption_font_key: 'montblack', caption_style: 'cherry', caption_size: 84, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'box', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'box', motion: 'rise', ms: 120, drop: 0.22, group: 6, line: 1.18, radius: 0.24, pad: [0.16, 0.04], glow: true, spot: [0.74, 0.8] },
  },
  {
    key: 'cherrypill',
    label: 'Cherry Pill',
    desc: 'A clean sentence. Each spoken word gets a rounded cherry pill.',
    engine: 'box',
    fields: { caption_font_key: 'heavy', caption_style: 'cherry', caption_size: 64, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'box', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'box', motion: 'rise', ms: 200, drop: 0.22, group: 6, line: 1.32, radius: 0.6, pad: [0.22, 0.02], spot: [0.72, 0.8] },
  },
  {
    key: 'kwmix',
    label: 'Keyword Mix',
    desc: 'Small clean words, then the key word lands big in an italic serif.',
    engine: 'kw',
    fields: { caption_font_key: 'plain', caption_style: 'yellow', caption_size: 48, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'color', caption_reveal: 'slide', caption_bounce: 'none' },
    opts: { group: 9, line: 1.12, reveal: 'rise', ms: 250, drop: 0.3, spot: [0.62, 0.8], key: { pick: 'one', font: 'playfair', scale: 2.15, own_line: true, line: 0.9, colour: 'active' } },
  },
  {
    key: 'kwpop',
    label: 'Keyword Pop',
    desc: 'A small white line, then the key word slams in big, italic and neon.',
    engine: 'kw',
    fields: { caption_font_key: 'heavy', caption_style: 'lime', caption_size: 48, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 5, caption_highlight: 'color', caption_reveal: 'slide', caption_bounce: 'none' },
    opts: { group: 5, line: 1.05, reveal: 'rise', ms: 180, drop: 0.25, spot: [0.64, 0.8], key: { pick: 'one', font: 'montitalic', scale: 1.9, own_line: true, colour: 'active', glow: 0.35, pop: true } },
  },
  {
    key: 'kwbox',
    label: 'Keyword Box',
    desc: 'A plain sentence builds word by word. Key words pop in a cherry pill.',
    engine: 'kw',
    fields: { caption_font_key: 'heavy', caption_style: 'cherry', caption_size: 64, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'box', caption_reveal: 'fade', caption_bounce: 'none' },
    opts: { group: 6, line: 1.3, reveal: 'fade', ms: 150, spot: [0.66, 0.8], key: { pick: 'all', italic: true, pop: true, pill: { pad: [0.28, 0.04], radius: 0.3, colour: 'active', text: '#FFFFFF' } } },
  },
  {
    key: 'serif',
    label: 'Elegant Serif',
    desc: 'Light thin words drift in. Key words are a fancy italic serif.',
    engine: 'kw',
    fields: { caption_font_key: 'light', caption_style: 'white', caption_size: 48, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'color', caption_reveal: 'fade', caption_bounce: 'none' },
    opts: { group: 6, line: 1.12, gap: 0.3, reveal: 'fade', ms: 400, spot: [0.74, 0.8], key: { pick: 'all', font: 'playfair', scale: 1.45, colour: 'base' } },
  },
  {
    key: 'hand',
    label: 'Handwritten',
    desc: 'Marker handwriting, slightly tilted. Key words get circled.',
    engine: 'kw',
    fields: { caption_font_key: 'marker', caption_style: 'yellow', caption_size: 84, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 5, caption_highlight: 'color', caption_reveal: 'slide', caption_bounce: 'none' },
    opts: { group: 5, line: 1.25, reveal: 'rise', ms: 200, drop: 0.15, tilt: -2, case: 'lower', spot: [0.66, 0.8], key: { pick: 'all', colour: 'active', circle: true } },
  },
  {
    key: 'label',
    label: 'Label',
    desc: 'Small typewriter capitals on a paper strip. Key words stamped in black.',
    engine: 'kw',
    fields: { caption_font_key: 'plexmono', caption_style: 'white', caption_size: 48, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 5, caption_highlight: 'color', caption_reveal: 'fade', caption_bounce: 'none' },
    opts: { group: 5, line: 1.12, gap: 0.7, spacing: 0.06, reveal: 'fade', ms: 120, spot: [0.76, 0.8], strip: { colour: '#F3EFE6', ink: '#111111', pad: [0.7, 0.4], radius: 0.12, tilt: -1 }, key: { pick: 'all', pill: { pad: [0.25, 0.0], radius: 0.0, colour: '#111111', text: '#F3EFE6' } } },
  },
  {
    key: 'cherryglow',
    label: 'Cherry Glow',
    desc: 'White capitals. The spoken word lights up cherry with a soft neon glow.',
    engine: 'box',
    fields: { caption_font_key: 'montblack', caption_style: 'cherry', caption_size: 84, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 3, caption_highlight: 'glow', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'glow', motion: 'fade', ms: 180, group: 3, line: 1.12, glow: 0.12, alpha: 0.85, spot: [0.7, 0.8] },
  },
  {
    key: 'cherryscript',
    label: 'Cherry Script',
    desc: 'Small clean words, then the key word lands in a glowing cherry serif.',
    engine: 'kw',
    fields: { caption_font_key: 'plain', caption_style: 'cherry', caption_size: 48, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 6, caption_highlight: 'color', caption_reveal: 'slide', caption_bounce: 'none' },
    opts: { group: 9, line: 1.12, reveal: 'rise', ms: 120, drop: 0.3, spot: [0.64, 0.8], key: { pick: 'one', font: 'playfair', scale: 2.2, own_line: true, line: 0.9, colour: 'active', glow: 0.25 } },
  },
  {
    key: 'glidebox',
    label: 'Glide Box',
    desc: 'Clean bold words. A cherry box slides behind each word.',
    engine: 'box',
    fields: { caption_font_key: 'poppinsblack', caption_style: 'cherry', caption_size: 108, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 4, caption_highlight: 'box', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'box', motion: 'glide', ms: 180, group: 4, line: 1.12, radius: 0.28, pad: [0.14, 0.04], spot: [0.7, 0.8] },
  },
  {
    key: 'underline',
    label: 'Underline Glide',
    desc: 'Condensed capitals. A cherry underline glides under the spoken word.',
    engine: 'box',
    fields: { caption_font_key: 'condensed', caption_style: 'cherry', caption_size: 84, caption_upper: true, caption_look: 'shadow', caption_mode: 'group', words_per_group: 3, caption_highlight: 'underline', caption_reveal: 'all', caption_bounce: 'none' },
    opts: { shape: 'underline', motion: 'glide', ms: 180, group: 3, line: 1.12, spot: [0.7, 0.8] },
  },
  {
    key: 'boldpop',
    label: 'Bold Pop',
    desc: 'Big capitals, two words at a time. The spoken word jumps up in yellow.',
    engine: null,
    fields: { caption_font_key: 'montblack', caption_style: 'yellow', caption_size: 84, caption_upper: true, caption_look: 'thick', caption_mode: 'group', words_per_group: 2, caption_highlight: 'pop', caption_reveal: 'all', caption_bounce: 'none' },
  },
  {
    key: 'chunky',
    label: 'Chunky Words',
    desc: 'One chunky yellow word at a time, thick black outline.',
    engine: null,
    fields: { caption_font_key: 'luckiest', caption_style: 'yellow', caption_size: 108, caption_upper: true, caption_look: 'thick', caption_mode: 'word', words_per_group: 1, caption_highlight: 'pop', caption_reveal: 'all', caption_bounce: 'none' },
  },
  {
    key: 'karaoke',
    label: 'Karaoke Fill',
    desc: 'Each word fills with yellow in time with the voice.',
    engine: null,
    fields: { caption_font_key: 'anton', caption_style: 'yellow', caption_size: 84, caption_upper: true, caption_look: 'outline', caption_mode: 'group', words_per_group: 3, caption_highlight: 'fill', caption_reveal: 'all', caption_bounce: 'none' },
  },
  {
    key: 'documentary',
    label: 'Documentary',
    desc: 'Calm serif words that fade in as they are said.',
    engine: null,
    fields: { caption_font_key: 'serif', caption_style: 'white', caption_size: 64, caption_upper: false, caption_look: 'shadow', caption_mode: 'group', words_per_group: 5, caption_highlight: 'color', caption_reveal: 'fade', caption_bounce: 'none' },
  },
];

export const presetByKey = (key) => CAPTION_PRESETS.find((p) => p.key === key) || null;
export const presetEngine = (key) => (presetByKey(key) || {}).engine || null;

/** The window's choices a preset IS: picking one of these yourself is your own mix (the preset
 *  is switched off). Font, colours, size, caps and look stay inside a preset.
 *  (export_style.PRESET_MOTION on the server.) */
export const PRESET_MOTION = ['mode', 'wordsPerGroup', 'highlightKey', 'revealKey', 'bounceKey'];

// ---------------------------------------------------------------------------------------------
// Story Stack — the worker's caption_fx.stack_plan / stack_events, same numbers.

export const STACK = {
  sizes: { S: 1.0, M: 1.5, L: 2.15 },   // × the caption size
  rowWords: { S: 3, M: 2, L: 1 },       // most words on one row
  block: [3, 6],                        // words per block: fewest worth a block of their own, most
  line: 1.02,                           // row pitch, in ems (line-height)
  gap: 0.26,                            // between two words on a row, in ems
  box: 0.8,                             // the block's box: this share of the frame's width
  spot: { portrait: [0.5, 0.47], landscape: [0.5, 0.5] },   // the box's centre, no drag
  rise: 0.22,                           // seconds a word takes to rise + fade in (ease-out)
  drop: 0.25,                           // ems it rises
};

export const SMALL_WORDS = new Set(`
a an the and or but so if of in on at to for from by with as into onto about than then
is are was were be been being am do does did done have has had will would can could should
shall may might must not no yes just very too also
i me my mine you your yours he him his she her hers it its we us our ours they them their
this that these those there here what who whom whose which how when where why
i'm i've i'll i'd you're you've you'll you'd he's she's it's we're we've we'll they're they've
they'll that's there's what's who's how's let's don't doesn't didn't can't won't isn't aren't
wasn't weren't im ive youre dont cant wont isnt
oh uh um like okay ok yeah
once still even ever really just gonna wanna gotta going got get gets some any all every each
more most much many other only own same such both out up down off over again now
`.split(/\s+/).filter(Boolean));

const PHRASE_END = /[.,!?;:…—]["'”’)\]]*$/;
const ALNUM = /[\p{L}\p{N}]/u;
const DIGIT = /\p{Nd}/u;

/** A word without the punctuation around it, lower case (curly apostrophes made straight). */
export const bare = (word) => {
  const w = String(word).replace(/’/g, "'").toLowerCase();
  const chars = Array.from(w);
  let i = 0;
  let j = chars.length;
  while (i < j && !ALNUM.test(chars[i])) i += 1;
  while (j > i && !ALNUM.test(chars[j - 1])) j -= 1;
  return chars.slice(i, j).join('');
};

/** projects → project, eyes → eye (AssemblyAI's nouns come lemmatised). */
export const stem = (b) => {
  let s = b.endsWith("'s") ? b.slice(0, -2) : b;
  if (Array.from(s).length > 3 && s.endsWith('s') && !s.endsWith('ss')) s = s.slice(0, -1);
  return s;
};

/** The clip's nouns (the ET row's `nouns`: 'project die project') as bare words + their stems. */
export const nounSet = (text) => {
  const out = new Set();
  String(text || '').split(/[,\s]+/).forEach((n) => {
    const b = bare(n);
    if (b) {
      out.add(b);
      out.add(stem(b));
    }
  });
  return out;
};

/** How much a word matters: 0 = a small word; longer words weigh more, nouns and numbers a lot. */
export const wordWeight = (word, nouns) => {
  const b = bare(word);
  const len = Array.from(b).length;
  const digit = DIGIT.test(b);
  if (!b || SMALL_WORDS.has(b) || (len <= 2 && !digit)) return 0;
  let w = 1 + Math.min(len, 12) / 10;
  if (digit) w += 3;
  if (nouns.has(b) || nouns.has(stem(b))) w += 3;
  return w;
};

/**
 * Words cut into blocks that follow the phrases: a block ends on . , ! ? ; : … —, a phrase
 * shorter than `lo` joins the one before (if the two fit in `hi`), a longer one than `hi` is
 * cut into even parts. Returns lists of word indices. (caption_fx.phrase_blocks)
 */
export const phraseBlocks = (words, lo, hi) => {
  const phrases = [];
  let cur = [];
  words.forEach((w, i) => {
    cur.push(i);
    if (PHRASE_END.test(String(w))) {
      phrases.push(cur);
      cur = [];
    }
  });
  if (cur.length) phrases.push(cur);
  const merged = [];
  phrases.forEach((p) => {
    const last = merged[merged.length - 1];
    if (last && (p.length < lo || last.length < lo) && last.length + p.length <= hi) {
      merged[merged.length - 1] = last.concat(p);
    } else {
      merged.push(p);
    }
  });
  const blocks = [];
  merged.forEach((p) => {
    const k = Math.ceil(p.length / hi);
    const base = Math.floor(p.length / k);
    const extra = p.length % k;
    let at = 0;
    for (let c = 0; c < k; c += 1) {
      const size = base + (c < extra ? 1 : 0);
      blocks.push(p.slice(at, at + size));
      at += size;
    }
  });
  return blocks;
};

/**
 * The Story Stack's blocks: one list of rows per block, a row = [size class, [word index]].
 * In a block (phraseBlocks, 3–6 words) the weightiest word is big (L, its own row), the other
 * weighty words medium (M, two to a row), small words share small rows (S).
 */
export const stackPlan = (words, nounsText = '') => {
  const nouns = nounSet(nounsText);
  return phraseBlocks(words, ...STACK.block).map((block) => {
    const weights = block.map((i) => wordWeight(words[i], nouns));
    let top = -1;
    weights.forEach((w, j) => {
      if (w > 0 && (top < 0 || w > weights[top])) top = j;
    });
    const rows = [];
    block.forEach((i, j) => {
      const cls = j === top ? 'L' : (weights[j] > 0 ? 'M' : 'S');
      const last = rows[rows.length - 1];
      if (last && last[0] === cls && last[1].length < STACK.rowWords[cls]) last[1].push(i);
      else rows.push([cls, [i]]);
    });
    return rows;
  });
};

export const easeOut = (u) => {
  const v = Math.min(1, Math.max(0, u));
  return 1 - (1 - v) ** 3;
};

export const easeInOut = (u) => {
  const v = Math.min(1, Math.max(0, u));
  return v < 0.5 ? 4 * v ** 3 : 1 - ((-2 * v + 2) ** 3) / 2;
};

// ---------------------------------------------------------------------------------------------
// The box family (Podcast Box, Soft Rise, Whisper, Cherry Box, Cherry Pill, Glide Box, Underline
// Glide) — the worker's caption_fx box_rect / box_state / box_events, same numbers.

export const BOX = {
  width: 0.86,          // the phrase's box: this share of the frame's width
  gap: 0.28,            // between two words, in ems
  fadeIn: 0.08,         // seconds a gliding box takes to appear on a phrase's first word
  underline: [0.02, 0.12],   // from the line box's bottom, this far up · this thick (ems)
};

/** A word's highlight rectangle [x, y, w, h] from its line box [x, line top, w, line height]. */
export const boxRect = (opts, [x, y, w, h], em) => {
  if (opts.shape === 'underline') return [x, y + h - BOX.underline[0] * em, w, BOX.underline[1] * em];
  const [px, py] = opts.pad || [0.14, 0.04];
  return [x - px * em, y - py * em, w + 2 * px * em, h + 2 * py * em];
};

/** The highlight boxes at `t` within one phrase (`block`: its word indices): [[x, y, w, h, alpha]]. */
export const boxState = (opts, rects, times, block, t, em) => {
  let k = -1;
  block.forEach((i, j) => { if (times[i] <= t) k = j; });
  if (k < 0) return [];
  const i = block[k];
  const age = t - times[i];
  const dur = Math.max(0.01, (opts.ms || 150) / 1000);
  const motion = opts.motion || 'glide';
  if (!rects[i]) return [];
  if (motion === 'glide') {
    if (k === 0) return [[...rects[i], easeOut(age / BOX.fadeIn)]];
    const e = easeInOut(age / dur);
    const a = rects[block[k - 1]] || rects[i];
    const b = rects[i];
    return [[0, 1, 2, 3].map((n) => a[n] + (b[n] - a[n]) * e).concat(1)];
  }
  const e = easeOut(age / dur);
  const out = [];
  if (k > 0 && e < 1 && rects[block[k - 1]]) out.push([...rects[block[k - 1]], 1 - e]);
  const drop = motion === 'rise' ? (opts.drop || 0.22) * em * (1 - e) : 0;
  const [x, y, w, h] = rects[i];
  out.push([x, y + drop, w, h, e]);
  return out;
};

/** Cherry Glow: how lit each word of the phrase is at `t` — [[word index, 0..1]]. */
export const glowState = (opts, times, block, t) => {
  let k = -1;
  block.forEach((i, j) => { if (times[i] <= t) k = j; });
  if (k < 0) return [];
  const e = easeOut((t - times[block[k]]) / Math.max(0.01, (opts.ms || 180) / 1000));
  const out = [];
  if (k > 0 && e < 1) out.push([block[k - 1], 1 - e]);
  out.push([block[k], e]);
  return out;
};

/** '#RRGGBB' part-way (e) from a to b. */
export const mixColour = (a, b, e) => {
  const x = String(a).replace('#', '');
  const y = String(b).replace('#', '');
  return `#${[0, 2, 4].map((n) => {
    const p = parseInt(x.slice(n, n + 2), 16);
    const q = parseInt(y.slice(n, n + 2), 16);
    return Math.round(p + (q - p) * e).toString(16).padStart(2, '0').toUpperCase();
  }).join('')}`;
};

// ---------------------------------------------------------------------------------------------
// The key-word family (Keyword Mix, Keyword Pop, Keyword Box, Elegant Serif, Handwritten, Label,
// Cherry Script) — the worker's caption_fx kw_keys / kw_enter / kw_events, same numbers.

export const KW = {
  pop: 0.3,                    // seconds a key word takes to pop in
  circle: 0.45,                // seconds the Handwritten circle takes to draw itself
  circleInset: [0.32, 0.18, 0.12],   // the circle reaches past the word: sides, top, bottom (ems)
  circleLine: 0.09,            // the circle's line, in ems
};

/** The phrase's key words: 'one' = the weightiest, 'all' = every noun or number in it (or the
 *  weightiest when there is none). A phrase of small words has none. */
export const kwKeys = (words, block, nouns, mode) => {
  const weights = block.map((i) => wordWeight(words[i], nouns));
  let top = -1;
  weights.forEach((w, j) => { if (w > 0 && (top < 0 || w > weights[top])) top = j; });
  if (top < 0) return new Set();
  if (mode === 'all') {
    const keys = new Set(block.filter((i, j) => weights[j] >= 3));
    return keys.size ? keys : new Set([block[top]]);
  }
  return new Set([block[top]]);
};

const backOut = (u) => {
  const v = Math.min(1, Math.max(0, u)) - 1;
  return 1 + 2.70158 * v ** 3 + 1.70158 * v ** 2;
};

/** A word's [alpha, rise in ems, scale] `age` seconds after it is said (before: invisible). */
export const kwEnter = (opts, key, age) => {
  if (age < 0) return [0, 0, 1];
  if (key && (opts.key || {}).pop) {
    const u = age / KW.pop;
    return [Math.min(1, u / 0.4), 0, 0.6 + 0.4 * backOut(u)];
  }
  const reveal = opts.reveal || 'rise';
  if (reveal === 'none') return [1, 0, 1];
  const e = easeOut(age / Math.max(0.01, (opts.ms || 250) / 1000));
  return [e, reveal === 'rise' ? (opts.drop || 0.3) * (1 - e) : 0, 1];
};
